import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { getFileUrl } from '../utils/fileUtils';
import Breadcrumbs from '../components/Breadcrumbs';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import './Servicios.css';

// Agrupa las secciones por "grupo" para la landing
function groupByGrupo(secciones) {
  const list = Object.values(secciones || {}).sort((a, b) => (a.orden || 0) - (b.orden || 0));
  const groups = {};
  list.forEach((s) => {
    const g = s.grupo || 'Otros';
    if (!groups[g]) groups[g] = [];
    groups[g].push(s);
  });
  return groups;
}

const Servicios = () => {
  const { data: config = {} } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      const response = await api.get('/configuracion');
      return response.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const { data: secciones = {}, isLoading: loadingDirectorio } = useQuery({
    queryKey: ['secciones-directorio'],
    queryFn: async () => {
      const res = await api.get('/secciones-directorio');
      return res.data;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const telefonoGeneral = String(config.telefono || '+57 315 466 9018')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[^\d+() \-]/g, '')
    .trim();
  const emailGeneral = String(config.email || 'diocesisdeipiales@gmail.com').trim();

  const grupos = groupByGrupo(secciones);
  const hasSecciones = Object.keys(grupos).length > 0;

  return (
    <div className="servicios-page page-container">
      <Breadcrumbs />
      <section className="section">
        <div className="container">
          <div className="page-header">
            <h1>Directorio / Servicios</h1>
            <p className="servicios-subtitle">
              Información y canales de contacto de la Curia diocesana, medios de comunicación y servicios.
            </p>
          </div>

          {loadingDirectorio && (
            <p className="servicios-loading">Cargando directorio...</p>
          )}

          {!loadingDirectorio && hasSecciones && (
            <div className="servicios-landing">
              {Object.entries(grupos).map(([nombreGrupo, items]) => (
                <div key={nombreGrupo} className="servicios-landing-group">
                  <h2 className="servicios-landing-group-title">{nombreGrupo}</h2>
                  <div className="servicios-landing-grid">
                    {items.map((item) => {
                      const slug = (item.clave || '').replace(/_/g, '-');
                      const esClero = item.clave === 'clero_diocesano';
                      const cardTo = esClero ? '/clero' : `/directorio/${slug}`;
                      return (
                        <Link key={item.clave} to={cardTo} className="servicios-landing-card">
                          {item.imagen_url && (
                            <div className="servicios-landing-card-img">
                              <img src={getFileUrl(item.imagen_url)} alt={item.titulo} />
                            </div>
                          )}
                          <h3 className="servicios-landing-card-title">{item.titulo}</h3>
                          {item.texto && (
                            <div className="servicios-landing-card-text" dangerouslySetInnerHTML={{ __html: item.texto.replace(/\n/g, '<br />') }} />
                          )}
                          {(item.telefono || item.direccion) && (
                            <div className="servicios-landing-card-contact">
                              {item.telefono && (
                                <a className="servicios-landing-contact-link" href={`tel:${String(item.telefono).replace(/[^\d+]/g, '')}`} onClick={e => e.stopPropagation()}>
                                  <FaPhone className="icon-phone-upright" aria-hidden="true" />
                                  <span>{item.telefono}</span>
                                </a>
                              )}
                              {item.direccion && (
                                <p className="servicios-landing-direccion">
                                  <FaMapMarkerAlt aria-hidden="true" />
                                  <span>{item.direccion}</span>
                                </p>
                              )}
                            </div>
                          )}
                          <span className="servicios-landing-card-more">Ver más →</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loadingDirectorio && !hasSecciones && (
            <p className="servicios-sin-datos">
              El directorio se está configurando. Puede usar el contacto general a continuación.
            </p>
          )}

          <div className="servicios-contacto">
            <h2 className="servicios-contacto-title">Contacto general</h2>
            <div className="servicios-contacto-row">
              <a className="servicios-contacto-item" href={`tel:${telefonoGeneral.replace(/[^\d+]/g, '')}`}>
                <FaPhone className="icon-phone-upright" aria-hidden="true" />
                <span>{telefonoGeneral}</span>
              </a>
              <a className="servicios-contacto-item" href={`mailto:${emailGeneral}`}>
                <FaEnvelope aria-hidden="true" />
                <span>{emailGeneral}</span>
              </a>
              <Link className="servicios-contacto-cta" to="/contacto">
                Ir a Contacto
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Servicios;
